import { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Card, Stack, Container, Typography, Box, Grid, Chip, Divider,
    CircularProgress, Button, TextField, Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Page from 'src/components/Page';
import syndicationApi from 'src/utils/syndicationApi';

const RootStyle = styled(Page)(({ theme }) => ({
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'flex-start',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    padding: theme.spacing(4, 0),
}));

const ContentCard = styled(Card)(({ theme }) => ({
    maxWidth: 800,
    width: '100%',
    padding: theme.spacing(4),
    borderRadius: 16,
    boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
}));

const statusColors = {
    active: 'success',
    approved: 'info',
    pending_approval: 'warning',
    pending_email_verification: 'warning',
    suspended: 'error',
    rejected: 'error',
};

export default function InvestorProfile() {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [formData, setFormData] = useState({});
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);

    const fetchProfile = () => {
        setLoading(true);
        syndicationApi.get('investor/profile/me/')
            .then((res) => {
                setProfile(res.data?.data);
                setFormData({
                    organization_name: res.data?.data?.organization_name || '',
                    bio: res.data?.data?.bio || '',
                    linkedin_url: res.data?.data?.linkedin_url || '',
                    sector_preferences: res.data?.data?.sector_preferences || '',
                    geographic_preferences: res.data?.data?.geographic_preferences || '',
                });
            })
            .catch(() => setProfile(null))
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchProfile(); }, []);

    const handleSave = async () => {
        setSaving(true);
        setMessage(null);
        try {
            const res = await syndicationApi.patch('investor/profile/update/', formData);
            setProfile(res.data?.data);
            setEditing(false);
            setMessage({ type: 'success', text: 'Profile updated successfully.' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to update profile.' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <RootStyle title="Profile | Gateway X Syndication">
                <Container maxWidth="md" sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
                    <CircularProgress sx={{ color: '#fff' }} />
                </Container>
            </RootStyle>
        );
    }

    return (
        <RootStyle title="Profile | Gateway X Syndication">
            <Container maxWidth="md" sx={{ display: 'flex', justifyContent: 'center' }}>
                <ContentCard>
                    <Stack spacing={3}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h4">My Profile</Typography>
                            <Stack direction="row" spacing={1}>
                                <Chip
                                    label={profile?.status?.replace(/_/g, ' ')}
                                    color={statusColors[profile?.status] || 'default'}
                                    size="small"
                                />
                                {!editing && (
                                    <Button variant="outlined" size="small" onClick={() => setEditing(true)}>
                                        Edit
                                    </Button>
                                )}
                            </Stack>
                        </Box>

                        {message && (
                            <Alert severity={message.type} onClose={() => setMessage(null)}>
                                {message.text}
                            </Alert>
                        )}

                        <Divider />

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary">Name</Typography>
                                <Typography variant="body1">
                                    {profile?.first_name} {profile?.last_name}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary">Email</Typography>
                                <Typography variant="body1">{profile?.email}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary">Phone</Typography>
                                <Typography variant="body1">{profile?.phone || '—'}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary">Investor Type</Typography>
                                <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                                    {profile?.investor_type?.replace(/_/g, ' ') || '—'}
                                </Typography>
                            </Grid>
                        </Grid>

                        <Divider />

                        {editing ? (
                            <Stack spacing={2}>
                                <TextField
                                    label="Organization Name"
                                    value={formData.organization_name}
                                    onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                                    fullWidth size="small"
                                />
                                <TextField
                                    label="Bio"
                                    value={formData.bio}
                                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                                    fullWidth multiline rows={3} size="small"
                                />
                                <TextField
                                    label="LinkedIn URL"
                                    value={formData.linkedin_url}
                                    onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                                    fullWidth size="small"
                                />
                                <TextField
                                    label="Sector Preferences"
                                    value={formData.sector_preferences}
                                    onChange={(e) => setFormData({ ...formData, sector_preferences: e.target.value })}
                                    fullWidth size="small"
                                />
                                <TextField
                                    label="Geographic Preferences"
                                    value={formData.geographic_preferences}
                                    onChange={(e) => setFormData({ ...formData, geographic_preferences: e.target.value })}
                                    fullWidth size="small"
                                />
                                <Stack direction="row" spacing={2}>
                                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                                        {saving ? 'Saving...' : 'Save Changes'}
                                    </Button>
                                    <Button variant="outlined" onClick={() => setEditing(false)} disabled={saving}>
                                        Cancel
                                    </Button>
                                </Stack>
                            </Stack>
                        ) : (
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">Organization</Typography>
                                    <Typography variant="body1">{profile?.organization_name || '—'}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">Bio</Typography>
                                    <Typography variant="body1">{profile?.bio || '—'}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="text.secondary">LinkedIn</Typography>
                                    <Typography variant="body1">{profile?.linkedin_url || '—'}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="text.secondary">Sector Preferences</Typography>
                                    <Typography variant="body1">{profile?.sector_preferences || '—'}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="text.secondary">Geographic Preferences</Typography>
                                    <Typography variant="body1">{profile?.geographic_preferences || '—'}</Typography>
                                </Grid>
                            </Grid>
                        )}

                        <Divider />
                        <Stack direction="row" spacing={2} justifyContent="center">
                            <Button component={RouterLink} to="/investor/dashboard" variant="text" size="small">
                                Dashboard
                            </Button>
                            <Button component={RouterLink} to="/investor/settings" variant="text" size="small">
                                Notification Settings
                            </Button>
                        </Stack>
                    </Stack>
                </ContentCard>
            </Container>
        </RootStyle>
    );
}
